import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const scrollRafRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  // Ferme le menu au changement de route
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Empêche le scroll du body quand le menu est ouvert
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Articles', href: '/articles' },
    { name: 'Études de cas', href: '/etudes-de-cas' },
    { name: 'Ressources', href: '/ressources' },
    { name: 'À propos', href: '/about' },
    { name: 'Partenaires', href: '/partners' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 print:hidden ${
        scrolled
          ? 'bg-dark-surface/95 backdrop-blur-md shadow-lg shadow-brand-sky/20 border-b border-brand-secondary/50'
          : 'bg-transparent'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="/images/logo-corpcore.png"
                alt="Corpcore"
                width="48"
                height="48"
                loading="eager"
                fetchpriority="high"
                className="h-10 w-10 lg:h-12 lg:w-12 brightness-0 invert drop-shadow-lg group-hover:brightness-100 group-hover:invert-0 transition-all duration-300"
              />
              <span className="font-bold text-xl lg:text-2xl text-white drop-shadow-md group-hover:text-brand-sky transition-colors">
                Corpcore
              </span>
            </Link>

            {/* Right side: CTA + burger */}
            <div className="flex items-center gap-3">
              <Link
                to="/contact"
                className="hidden sm:inline-flex btn-primary shadow-lg shadow-brand-secondary/30 text-sm py-2 px-4"
              >
                Prendre rendez-vous
              </Link>

              {/* Burger button */}
              <button
                className="p-2 rounded-xl hover:bg-dark-hover transition-colors text-white"
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Overlay — exclut la zone du panneau pour éviter tout conflit de z-index */}
      {isOpen && (
        <div
          className="fixed inset-y-0 left-0 right-72 sm:right-80 z-[60] bg-black/60"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-in menu panel */}
      <div className={`print:hidden fixed top-0 right-0 h-full w-72 sm:w-80 z-[70] bg-dark-surface border-l border-border shadow-2xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 h-16 lg:h-20 border-b border-border">
          <span className="font-bold text-white text-lg">Menu</span>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-xl hover:bg-dark-hover transition-colors text-white"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav links */}
        <div className="overflow-y-auto h-[calc(100%-80px)] px-4 py-6 flex flex-col gap-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setIsOpen(false)}
              className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-all ${
                location.pathname === item.href
                  ? 'bg-purple-600/30 text-white border border-purple-500/40'
                  : 'text-white/80 hover:text-white hover:bg-dark-hover'
              }`}
            >
              {item.name}
              <ChevronRight size={16} className="opacity-40" />
            </Link>
          ))}

          {/* Séparateur */}
          <div className="border-t border-border my-4" />

          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="btn-primary text-center block"
          >
            Prendre rendez-vous
          </Link>
        </div>
      </div>
    </>
  );
};

export default Header;
